// <bubble-map-chart> — the revenue-by-territory world map: one accent
// bubble per resolved territory, sized by net, over muted land silhouettes.
// Same contract as the other chart elements: JSON payload script + sized
// canvas wrapper, element only draws. The ranked country list beside it is
// server-rendered HTML.
//
//   <bubble-map-chart>
//     <script type="application/json" data-chart-data>{...}</script>
//     <div class="relative h-80"><canvas></canvas></div>
//   </bubble-map-chart>
//
// Coordinates arrive in the payload (the server resolves ISO codes to
// centroids via the countries gem), so the element only needs the land
// outline — world-atlas's 110m TopoJSON, bundled at build time.

import { Chart, PointElement } from "chart.js";
import {
	BubbleMapController,
	GeoFeature,
	ProjectionScale,
	SizeScale,
	topojson,
} from "chartjs-chart-geo";
import worldAtlas from "world-atlas/land-110m.json";
import {
	chartColor,
	ensureChartTheme,
	findCanvas,
	money,
	readPayload,
	withAlpha,
} from "./chart-theme";

type Payload = {
	currency: string;
	points: {
		label: string;
		latitude: number;
		longitude: number;
		value: number;
	}[];
};

// The geo controller/scales register lazily on first map, so statements
// without a territory breakdown never pay for them.
let geoRegistered = false;
function ensureGeoRegistered() {
	if (geoRegistered) return;
	geoRegistered = true;
	Chart.register(
		BubbleMapController,
		GeoFeature,
		ProjectionScale,
		SizeScale,
		PointElement,
	);
}

const atlas = worldAtlas as unknown as Parameters<typeof topojson.feature>[0];
// topojson.feature returns a single Feature for land-110m's MultiPolygon;
// the dataset's outline option wants an array either way.
const landFeature = topojson.feature(atlas, atlas.objects.land);
const land = "features" in landFeature ? landFeature.features : [landFeature];

class BubbleMapChart extends HTMLElement {
	#chart: Chart<"bubbleMap"> | null = null;

	connectedCallback() {
		ensureChartTheme();
		ensureGeoRegistered();
		const payload = readPayload<Payload>(this, "bubble-map-chart");
		const accent = chartColor("--color-accent");

		this.#chart?.destroy();
		this.#chart = new Chart(findCanvas(this, "bubble-map-chart"), {
			type: "bubbleMap",
			data: {
				labels: payload.points.map((point) => point.label),
				datasets: [
					{
						outline: land,
						showOutline: true,
						outlineBackgroundColor: "rgba(255, 255, 255, 0.04)",
						outlineBorderColor: "rgba(255, 255, 255, 0.09)",
						outlineBorderWidth: 1,
						backgroundColor: withAlpha(accent, 0.5),
						borderColor: withAlpha(accent, 0.9),
						hoverBackgroundColor: withAlpha(accent, 0.75),
						borderWidth: 1,
						data: payload.points.map(({ latitude, longitude, value }) => ({
							latitude,
							longitude,
							value,
						})),
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				animation: { duration: 300 },
				plugins: {
					tooltip: {
						callbacks: {
							// chartjs-chart-geo derives item.label from the parsed point
							// (the longitude!) rather than data.labels, so the name is
							// looked up in the payload by index instead.
							label: (item) => {
								const point = payload.points[item.dataIndex];
								if (!point) return "";
								return ` ${point.label}: ${money(point.value, payload.currency)}`;
							},
						},
					},
				},
				scales: {
					projection: { axis: "x", projection: "naturalEarth1" },
					size: { axis: "x", range: [5, 24], display: false },
				},
			},
		});
	}

	disconnectedCallback() {
		this.#chart?.destroy();
		this.#chart = null;
	}
}

if (!customElements.get("bubble-map-chart")) {
	customElements.define("bubble-map-chart", BubbleMapChart);
}
